<script setup lang="ts">
import { ref, computed, reactive, watch } from "vue";
import { storeToRefs } from "pinia";
import type {
  Sticker,
  StickerCollectionTag,
  StickerUpdateParams,
} from "@/types/stickers";
import { createEmptySticker } from "@/types/stickers";
import { userStickers } from "@/stores/stickers";
import { useTagManagementStore } from "@/stores/tagManagement";
const stickerStore = userStickers();
const tagManagementStore = useTagManagementStore();
import {
  close_small,
  arrow_right,
  arrow_left,
} from "pn-design-assets/pn-assets/icons";

const createDefaultStickerParams = (): StickerUpdateParams => ({
  collectionId: 0,
  swedishAltText: "",
  englishAltText: "",
  categories: [],
  englishTags: [],
  swedishTags: [],
  stickerId: 0,
});

const stickerUpdateParams = reactive<StickerUpdateParams>(
  createDefaultStickerParams(),
);
const { notificationObj, currentCollection } = storeToRefs(stickerStore);
const isSaveButtonClicked = ref(false);
const currentStep = ref(1);
const TOTAL_STEPS = 3;
const colorProminent = ref<boolean | null>(null);
const onStickerTextPresence = ref<boolean | null>(null);
const selectedColorCategoryId = ref<number | null>(null);
const initialColorCategoryId = ref<number | null>(null);
const selectedLanguageCategoryId = ref<number | null>(null);
const dominantColorOptions = computed(
  () => tagManagementStore.getDominantColorOptions,
);
const hasDominantColorChanged = computed(
  () => selectedColorCategoryId.value !== initialColorCategoryId.value,
);

const props = defineProps<{
  open?: boolean;
  sticker?: Sticker;
  showLoader?: boolean;
}>();

const open = computed(() => props.open ?? false);
const sticker = computed(() => props.sticker ?? createEmptySticker());
const showLoader = computed(() => props.showLoader ?? false);

const englishTags = () => {
  const tags = sticker.value.tags ?? [];
  const englishTagObj = tags.find(
    (t: StickerCollectionTag) => t.language === "english",
  );
  return englishTagObj?.tags ?? [];
};

const swedishTags = () => {
  const tags = sticker.value.tags ?? [];
  const swedishTagObj = tags.find(
    (t: StickerCollectionTag) => t.language === "swedish",
  );
  return swedishTagObj?.tags ?? [];
};

const getEnglishAltText = () => {
  const stickerDesc = sticker.value.stickerDesc ?? [];
  const descriptionObj = stickerDesc.find((stickerDescObj) => {
    return stickerDescObj.language === "english";
  });
  return descriptionObj?.description ?? "";
};

const getSwedishAltText = () => {
  const stickerDesc = sticker.value.stickerDesc ?? [];
  const descriptionObj = stickerDesc.find((stickerDescObj) => {
    return stickerDescObj.language === "swedish";
  });
  return descriptionObj?.description ?? "";
};

const reset = () => {
  Object.assign(stickerUpdateParams, createDefaultStickerParams());
  isSaveButtonClicked.value = false;
  currentStep.value = 1;
  colorProminent.value = null;
  selectedColorCategoryId.value = null;
  initialColorCategoryId.value = null;
};

const emit = defineEmits<{
  (e: "onClose"): void;
  (e: "onSave", payload: StickerUpdateParams): void;
}>();

watch(open, (now, prev) => {
  if (now === true && prev !== true) {
    if (sticker.value.completed) {
      stickerUpdateParams.englishTags = englishTags();
      stickerUpdateParams.swedishTags = swedishTags();
      stickerUpdateParams.englishAltText = getEnglishAltText();
      stickerUpdateParams.swedishAltText = getSwedishAltText();
      const dominantColorIds = dominantColorOptions.value.map(
        (option) => option.categoryId,
      );
      const existingColorId = (sticker.value.tagCategoryIds ?? []).find(
        (taggedCategoryId) => dominantColorIds.includes(taggedCategoryId),
      );
      colorProminent.value = existingColorId !== undefined;
      selectedColorCategoryId.value = existingColorId ?? null;
      initialColorCategoryId.value = existingColorId ?? null;
    }
  }

  if (now === false && prev === true) {
    reset();
  }
});

const onClose = () => {
  reset();
  emit("onClose");
};

const setColorProminent = (value: boolean) => {
  colorProminent.value = value;
  if (!value) {
    selectedColorCategoryId.value = null;
  }
};

/* const onSetTextPresence = (value: boolean) => {
  onStickerTextPresence.value = value;
  if (!value) {
    selectedLanguageCategoryId.value = null;
  }
}; */

const validateColorStep = () => {
  stickerStore.populateNotificationObj({
    show: false,
    message: "",
    status: "",
  });
  if (colorProminent.value === null) {
    stickerStore.populateNotificationObj({
      show: true,
      message: "Please answer whether one color is very prominent",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
    return false;
  }
  if (colorProminent.value === true && selectedColorCategoryId.value === null) {
    stickerStore.populateNotificationObj({
      show: true,
      message: "Please select a color",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
    return false;
  }
  return true;
};

const goToNextStep = () => {
  if (currentStep.value === 1 && !validateColorStep()) {
    return;
  }
  if (currentStep.value === 3 && !validateTags()) {
    return;
  }
  currentStep.value += 1;
};

const goToPreviousStep = () => {
  currentStep.value -= 1;
};

const onSave = async () => {
  isSaveButtonClicked.value = true;
  if (validateAltText()) {
    const payload: StickerUpdateParams = {
      collectionId: stickerStore.currentCollection.collectionId,
      swedishAltText: stickerUpdateParams.swedishAltText,
      englishAltText: stickerUpdateParams.englishAltText,
      categories: [
        ...stickerStore.currentCollection.categories,
        ...(colorProminent.value && selectedColorCategoryId.value !== null
          ? [selectedColorCategoryId.value]
          : []),
        ...(onStickerTextPresence.value &&
        selectedLanguageCategoryId.value !== null
          ? [selectedLanguageCategoryId.value]
          : []),
      ],
      englishTags: [...stickerUpdateParams.englishTags],
      swedishTags: [...stickerUpdateParams.swedishTags],
      stickerId: sticker.value.stickerId,
      languages: stickerStore.currentCollection.languages,
      completed: true,
    };
    emit("onSave", payload);
  }
};

const validateTags = () => {
  stickerStore.populateNotificationObj({
    show: false,
    message: "",
    status: "",
  });
  if (
    stickerUpdateParams.englishTags.length === 0 ||
    stickerUpdateParams.swedishTags.length === 0
  ) {
    stickerStore.populateNotificationObj({
      show: true,
      message: "Please select atleast one english and one swedish tag",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
    return false;
  }
  return true;
};

const validateAltText = () => {
  return (
    stickerUpdateParams.englishAltText !== "" &&
    stickerUpdateParams.swedishAltText !== ""
  );
};
const getTags = (language: string) => {
  const tags = sticker.value.collectionTags ?? [];
  const languageTagObj = tags.find(
    (t: StickerCollectionTag) => t.language === language,
  );
  const collectionTags = languageTagObj?.tags ?? [];
  const resolvedCategoryStickerTags = hasDominantColorChanged.value
    ? []
    : language === "swedish"
      ? sticker.value.resolvedCategoryTags?.swedish ?? []
      : sticker.value.resolvedCategoryTags?.english ?? [];
  const resolvedCategoryCollectionTags =
    language === "swedish"
      ? currentCollection.value.resolvedCategoryTags?.swedish ?? []
      : currentCollection.value.resolvedCategoryTags?.english ?? [];
  return [
    ...collectionTags,
    ...resolvedCategoryStickerTags,
    ...resolvedCategoryCollectionTags,
    ...getSelectedColorTags(language),
  ];
};

const getSelectedColorTags = (language: string): string[] => {
  const selectedColorOption = dominantColorOptions.value.find(
    (option) => option.categoryId === selectedColorCategoryId.value,
  );
  return (
    selectedColorOption?.tags.find((tag) => tag.language === language)?.tags ??
    []
  );
};

const onTagEntered = (event: any, language: string) => {
  const tag: string = event.target.value;
  if (language === "swedish" && !checkIfTagExists(tag, "swedish")) {
    stickerUpdateParams.swedishTags.push(tag);
  } else if (language === "english" && !checkIfTagExists(tag, "english")) {
    stickerUpdateParams.englishTags.push(tag);
  }
  event.target.value = "";
};

const checkIfTagExists = (tag: string, language: string) => {
  const allTags = getTags(language);
  const tagExists = allTags.indexOf(tag) >= 0;
  if (tagExists) {
    stickerStore.populateNotificationObj({
      show: true,
      message: "Tag already exists in the list",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  }
  return tagExists;
};

const setAltText = (altText: string, language: string) => {
  if (language === "swedish") {
    stickerUpdateParams.swedishAltText = altText;
  } else if (language === "english") {
    stickerUpdateParams.englishAltText = altText;
  }
};

const getAltTextError = (language: string) => {
  if (isSaveButtonClicked.value) {
    if (language === "swedish" && stickerUpdateParams.swedishAltText === "") {
      return "Swedish alternative text is required";
    } else if (
      language === "english" &&
      stickerUpdateParams.englishAltText === ""
    ) {
      return "English alternative text is required";
    }
  }
};

const removeTag = (tag: string, language: string) => {
  if (language === "swedish") {
    const index = stickerUpdateParams.swedishTags.indexOf(tag);
    if (index > -1) {
      stickerUpdateParams.swedishTags.splice(index, 1);
    }
  } else if (language === "english") {
    const index = stickerUpdateParams.englishTags.indexOf(tag);
    if (index > -1) {
      stickerUpdateParams.englishTags.splice(index, 1);
    }
  }
};

/* const setSelectedLanguageCategoryId = (categoryId: number) => {
  selectedLanguageCategoryId.value = categoryId;
}; */
</script>
<template>
  <pn-modal :open="open" class="large-modal spinner-modal" @close="onClose">
    <div class="modal-spinner-overlay" v-show="showLoader">
      <pn-spinner size="3" />
    </div>
    <h2 class="pn-l">Make sticker searchable and accessible</h2>
    <p class="pn-s modal-subtitle">
      In order to make sticker easy for users to find as well as for vision
      impaired to &ldquo;see&rdquo;, complete the four steps below.
    </p>
    <div class="step-indicator">
      <pn-progress-stepper
        label="Steps"
        :total-steps="TOTAL_STEPS"
        :current-step="currentStep"
      ></pn-progress-stepper>
    </div>
    <!-- Step 1: Dominant color -->
    <section v-if="currentStep === 1" class="flex flex-direction-column">
      <div class="flex align-items-start">
        <img class="thumb" :src="sticker.thumbUrl" :alt="sticker.thumbUrl" />
      </div>
      <h3 class="pn-m">Is one color very prominent?</h3>
      <p class="pn-s width-67">
        Only check this if one color is very prominent (making up AT LEAST 50%
        of the sticker). In most cases you may leave this unchecked.
      </p>
      <div class="info-box" role="note">
        <div class="info-box-icon" aria-hidden="true">&#9432;</div>
        <div>
          <p class="pn-s">
            <strong>Helpful question to ask yourself:</strong>
          </p>
          <p class="pn-s">
            If searching for stickers of a certain color, would I want this
            sticker to show up?
          </p>
        </div>
      </div>
      <label for="color-prominent-yes">
        <pn-radio-button
          radioid="color-prominent-yes"
          name="colorProminent"
          value="yes"
          :checked="colorProminent === true"
          @change="setColorProminent(true)"
        />
        Yes
      </label>
      <label for="color-prominent-no">
        <pn-radio-button
          radioid="color-prominent-no"
          name="colorProminent"
          value="no"
          :checked="colorProminent === false"
          @change="setColorProminent(false)"
        />
        No
      </label>
      <div v-if="colorProminent === true" class="flex flex-direction-column">
        <p class="pn-s color-select-label">Select color</p>
        <div class="flex flex-wrap gap-16">
          <label
            v-for="option in dominantColorOptions"
            :key="option.categoryId"
            :for="`dominant-color-${option.categoryId}`"
          >
            <pn-radio-button
              :radioid="`dominant-color-${option.categoryId}`"
              name="dominantColor"
              :value="option.categoryId"
              :checked="selectedColorCategoryId === option.categoryId"
              @change="selectedColorCategoryId = option.categoryId"
            />
            {{ option.enCategoryName }}
          </label>
        </div>
      </div>
    </section>
    <!-- Language selection  commented out 
    <section v-if="currentStep === 2" class="flex flex-direction-column">
      <div class="flex align-items-start">
        <img class="thumb" :src="sticker.thumbUrl" :alt="sticker.thumbUrl" />
      </div>
      <h3 class="pn-m">Does the sticker have text?</h3>
      <label for="sticker-has-text-yes">
        <pn-radio-button
          radioid="sticker-has-text-yes"
          name="stickerHasText"
          value="yes"
          :checked="onStickerTextPresence === true"
          @change="onSetTextPresence(true)"
        />
        Yes
      </label>
      <label for="sticker-has-text-no">
        <pn-radio-button
          radioid="sticker-has-text-no"
          name="stickerHasText"
          value="no"
          :checked="onStickerTextPresence === false"
          @change="onSetTextPresence(false)"
        />
        No
      </label>
      <div v-if="onStickerTextPresence" class="flex flex-direction-column">
        <div class="flex">
          <pn-input label="Enter the full text"></pn-input>
        </div>
        <p class="pn-s color-select-label">What is the language</p>
        <div class="flex flex-wrap gap-16">
          <label
            v-for="option in tagManagementStore.getLanguageCategoryOptions"
            :key="option.categoryId"
            :for="`language-category-${option.categoryId}`"
          >
            <pn-radio-button
              :radioid="`language-category-${option.categoryId}`"
              name="languageCategory"
              :value="option.categoryId"
              :checked="selectedLanguageCategoryId === option.categoryId"
              @change="setSelectedLanguageCategoryId(option.categoryId)"
            />
            {{ option.enCategoryName }}
          </label>
        </div>
      </div>
    </section> -->
    <!-- Step 2: Tags -->
    <section v-if="currentStep === 2" class="flex flex-direction-column">
      <div class="flex align-items-start">
        <img class="thumb" :src="sticker.thumbUrl" :alt="sticker.thumbUrl" />
      </div>
      <h3 class="pn-m">Add additional tags to describe the sticker</h3>
      <p class="pn-s width-67">
        Below you can see the tags already added to the sticker (by your
        selections in the previous steps as well as tags given to the whole
        collection). Are there additional words you could use to describe this
        specific sticker?
      </p>
      <div class="info-box" role="note">
        <div class="info-box-icon" aria-hidden="true">&#9432;</div>
        <div>
          <p class="pn-s">
            <strong>Helpful questions to ask yourself:</strong>
          </p>
          <ul class="pn-s">
            <li>
              What does the sticker depict? Is there more than one word for
              this?
            </li>
            <li>
              If a character, what mood or feeling does it express (if obvious)?
            </li>
            <li>
              Would a user want to see this sticker if searching for stickers of
              a certain color? If so, what color?
            </li>
            <li>Does it have any text? If so, what does it say?</li>
            <li>
              What additional words (if any) would I use if searching for this
              sticker?
            </li>
          </ul>
        </div>
      </div>
      <div class="flex justify-space-between gap-32">
        <div class="flex flex-direction-column flex-1 button">
          <pn-input
            label="Swedish tag"
            helpertext="Enter one word at a time separated by blank space."
            @keydown.space.prevent="onTagEntered($event, 'swedish')"
          ></pn-input>
          <div class="flex gap-4 flex-wrap tag-list">
            <span class="pn-s tag-label">Tags:</span>
            <pn-tag
              :icon="close_small"
              small
              :label="tag"
              v-for="(tag, index) in stickerUpdateParams.swedishTags"
              :key="`sv-tag-${index}`"
              @click="removeTag(tag, 'swedish')"
            ></pn-tag>
            <pn-tag
              small
              appearance="discrete"
              :label="tag"
              v-for="(tag, index) in getTags('swedish')"
              :key="`collection-sv-tag-${index}`"
            ></pn-tag>
          </div>
        </div>
        <div class="flex flex-direction-column button">
          <pn-input
            label="English tag"
            helpertext="Enter one word at a time separated by blank space."
            @keydown.space.prevent="onTagEntered($event, 'english')"
          ></pn-input>
          <div class="flex gap-4 flex-wrap tag-list">
            <span class="pn-s tag-label">Tags:</span>
            <pn-tag
              small
              :label="tag"
              :icon="close_small"
              v-for="(tag, index) in stickerUpdateParams.englishTags"
              :key="`en-tag-${index}`"
              @click="removeTag(tag, 'english')"
            ></pn-tag>
            <pn-tag
              small
              appearance="discrete"
              :label="tag"
              v-for="(tag, index) in getTags('english')"
              :key="`collection-en-tag-${index}`"
            ></pn-tag>
          </div>
        </div>
      </div>
    </section>
    <!-- Step 4: Alt text -->
    <section v-if="currentStep === 3" class="flex flex-direction-column">
      <div class="flex align-items-start">
        <img class="thumb" :src="sticker.thumbUrl" :alt="sticker.thumbUrl" />
      </div>
      <h3 class="pn-m">Make sticker accessible (with alt text)</h3>
      <p class="pn-s description">
        Describe the sticker in a way that quickly gives a visually impaired
        user an idea of what the image depicts.
      </p>
      <div class="flex justify-space-between gap-32">
        <div class="flex flex-direction-column button">
          <pn-input
            label="Swedish"
            :value="stickerUpdateParams.swedishAltText"
            helpertext="Example: Elefant med partyhat OR dansande apa"
            @input="setAltText($event.target.value, 'swedish')"
            :error="getAltTextError('swedish')"
          ></pn-input>
        </div>
        <div class="flex flex-direction-column button">
          <pn-input
            label="English"
            :value="stickerUpdateParams.englishAltText"
            helpertext="Example: Elephant with party hat OR dancing monkey"
            @input="setAltText($event.target.value, 'english')"
            :error="getAltTextError('english')"
          ></pn-input>
        </div>
      </div>
    </section>
    <pn-toast
      class="modal-bottom-toast"
      :appearance="notificationObj.status"
      v-show="notificationObj.show"
      :text="notificationObj.message"
      temporary
    ></pn-toast>
    <!-- eslint-disable vue/no-deprecated-slot-attribute -->
    <div slot="buttons">
      <template v-if="currentStep > 1">
        <pn-button
          appearance="light"
          variant="outlined"
          :icon="arrow_left"
          @click="goToPreviousStep"
          >Previous</pn-button
        >
      </template>
      <template v-if="currentStep < TOTAL_STEPS">
        <pn-button
          :icon="arrow_right"
          icon-alignment="right"
          @click="goToNextStep"
          >Next</pn-button
        >
      </template>
      <template v-else>
        <pn-button @click="onSave">Save and close</pn-button>
      </template>
    </div>
  </pn-modal>
</template>
<style>
.thumb {
  height: 120px;
  width: auto;
}

.modal-subtitle {
  margin-bottom: 8px;
}

.step-indicator {
  margin-bottom: 16px;
}

.info-box {
  display: flex;
  gap: 12px;
  background-color: #e8f4fb;
  border-radius: 4px;
  padding: 12px 16px;
  margin: 12px 0;
  max-width: 720px;
}

.info-box-icon {
  font-size: 18px;
  color: #0072b5;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-box ul {
  margin: 4px 0 0 0;
  padding-left: 20px;
}

.info-box li {
  margin-bottom: 4px;
}

.tag-list {
  align-items: center;
  margin-top: 8px;
}

.tag-label {
  font-weight: 600;
}

.color-select-label {
  font-weight: 600;
}

.large-modal .pn-modal {
  max-width: 80% !important;
  width: 80% !important;
}

.button {
  width: 50%;
}
.button pn-input {
  width: 100%;
}

.modal-spinner-overlay {
  --modal-content-padding: 24px;
  position: sticky;
  top: calc(-1 * var(--modal-content-padding));
  left: 0;
  width: calc(100% + (2 * var(--modal-content-padding)));
  height: 100vh;
  margin-left: calc(-1 * var(--modal-content-padding));
  margin-bottom: -100vh;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: all;
  z-index: 2147483647;
}

.modal-bottom-toast {
  margin: 12px auto 0;
  width: fit-content;
  max-width: calc(100% - 32px);
  text-align: center;
}

.description {
  max-width: 720px;
}
</style>
